import React, { } from 'react';
import { useSign, CHANGE_PREVIEW, useSignState } from 'context/signview';
import { useTranslation } from 'react-i18next';
import { useSlate } from 'slate-react';
import { Node } from 'slate';

import { initialLetters, parseDate } from 'utils';
import { getNodesByProperty } from '../../Editor/Canvas/TratoFormat';
import {
    UncontrolledCollapse,
    InputGroup,
    InputGroupAddon,
    InputGroupText,
    Media,
    Button,
    Card,
    ListGroup,
    ListGroupItem
} from 'reactstrap';
import { createMarkup } from '../SignView';

export function VersionLight({ version = {}, participant = {} }) {
    const { name, last_name, _id } = participant
    const { t } = useTranslation();
    return (
        <ListGroupItem className="d-flex text-center p-0">
            <div style={{ width: '40px' }} className="border-right align-self-center p-2">
                <div>
                    {initialLetters(name, last_name)}
                </div>
            </div>
            <div className="flex-grow-1 align-self-center p-1">
                {version.accepted?.includes(_id) &&
                    <><i className="fas fa-circle text-success" /><span>{' '}{t('sign.contract.accepted')}</span></>
                }
                {version.rejected?.includes(_id) &&
                    <><i className="fas fa-circle text-danger" /><span>{' '}{t('sign.contract.rejected')}</span></>
                }
                {version.review?.includes(_id) &&
                    <><i className="fas fa-circle text-warning" /><span>{' '}{t('sign.contract.review')}</span></>
                }
            </div>
        </ListGroupItem>
    )
}

function Light({ light, elements, participants = [], isMigrated }) {
    const { t } = useTranslation();
    return (
        <Card className="p-4 bg-light">
            <Media>
                <Media body className="font-weight-bold align-self-center">
                    {t(`sign.negotiation.${light.name}`)}{' '}({elements.filter(e => e.type === light.type).length})
                </Media>
                <Media middle right href="#" className="align-self-center">
                    <Button size="sm" color="light" id={`expand-${light.name}`}><i className="fas fa-caret-right"></i></Button>
                </Media>
            </Media>
            <UncontrolledCollapse toggler={`#expand-${light.name}`} className="mt-4">
                {elements
                    .filter(e => e.isNegotiable && e.type === light.type && e.isAccepted === light.accepted)
                    .map((element) => (
                        <Card key={element.name} color="white">
                            <div className="clean-text bold-text border-bottom p-3 mb-3">
                                {
                                    isMigrated ? Node.string(element)
                                        : <div className="text-truncate" dangerouslySetInnerHTML={createMarkup(element.content)} />
                                }

                            </div>
                            <ListGroup>
                                {participants.filter(p => !p.isNotNegotiator).map((p) => (
                                    <VersionLight
                                        key={p._id}
                                        participant={p}
                                        version={element.versions.find((v) => v._id === element.currentVersionId)}
                                    />
                                ))}
                            </ListGroup>
                        </Card>
                    ))}
            </UncontrolledCollapse>
        </Card>
    )
}

function WordLight({ version = {}, participants = [], currentVersion }) {

    const { t } = useTranslation();

    const [, dispatch] = useSign();

    const { versionNumber, user, url, modified } = version;

    const handlePreviewVersion = () => {
        dispatch({
            type: CHANGE_PREVIEW,
            previewUrl: url,
            previewMode: currentVersion !== version.versionId
        })
    }

    return (
        <Card className="p-4 bg-light align-self-center">
            <Media tag="li" className="">
                <Media body className="font-weight-bold align-self-center">
                    <p>
                        <Button
                            color="default"
                            onClick={handlePreviewVersion}>
                            <i className="fas fa-eye"></i>
                        </Button>{'  '}
                        {t('contracts.negotiation.version')}: {versionNumber + 1}</p>
                    <p>{t('contracts.negotiation.autor')}: {user}</p>
                    <p className="font-weight-bold">
                        {parseDate(modified, 'Pp')}{ }
                    </p>
                </Media>
                <Media middle right href="#" className="align-self-center">
                    <Button size="sm" color="light" id={`expand-${version._id}`}><i className="fas fa-caret-right"></i></Button>
                </Media>
            </Media>
            <UncontrolledCollapse toggler={`#expand-${version._id}`}>
                <Card color="white">
                    {participants.map((p) =>
                        <VersionLight
                            participant={p}
                            version={version}
                        />
                    )}
                </Card>
            </UncontrolledCollapse>
        </Card>
    )
}

const NegotiationTab = React.memo(function NegotiationTabElement() {

    const { t } = useTranslation();

    const { contract } = useSignState();
    const editor = useSlate();

    const participants = contract.participants || [];
    const elements = (contract.isMigrated ? getNodesByProperty(editor, {
        property: 'isNegotiable',
        value: true
    }).map(([n]) => n) : contract.elements) || [];
    const wordonline = contract.wordonline || {};

    const lights = [
        { name: 'clausesnegotiable', type: 'clause', accepted: false },
        { name: 'statementsnegotiable', type: 'statement', accepted: false },
        { name: 'textsnegotiable', type: 'text', accepted: false },
        { name: 'textsaccepted', type: 'text', accepted: true },
        { name: 'clausesaccepted', type: 'clause', accepted: true },
        { name: 'statementsaccepted', type: 'statement', accepted: true },
    ];
    // TODO CHECK ISACCEPTED

    const wordonlineVersions = wordonline.versions?.length || false;

    return (
        <>
            {wordonlineVersions &&
                <div className="mb-3">
                    <span>
                        {t('contracts.negotiation.existing')}: <b>{wordonlineVersions}</b>
                    </span>
                </div>
            }
            {/* TRATO FORMAT */}
            {elements.length ? lights.map((light) =>
            (elements.filter(e => e.isNegotiable && e.type === light.type && e.isAccepted === light.accepted).length ?
                <Light
                    light={light}
                    elements={elements}
                    participants={participants}
                    isMigrated={contract.isMigrated}
                />
                : null
            )) : null}

            {/* WORD FORMAT */}
            {wordonlineVersions && wordonline.versions.map((version) => (
                <WordLight
                    currentVersion={wordonline.Version}
                    version={version}
                    participants={participants}
                />
            ))}
        </>
    );
});

export default NegotiationTab;